import React from 'react';

import { QRCodeSVG } from 'qrcode.react';
import { c } from 'ttag';

import { OtpInput } from '@freelancelabs/components';

export const TOTP_CODE_LENGTH = 6;

interface Props {
    /** `otpauth://` URI the authenticator app scans. */
    uri: string;
    /** The shared secret, for whoever cannot scan. Grouped for reading, see below. */
    secret: string;
    code: string;
    onCode: (code: string) => void;
    /** Message under the code boxes. Empty when nothing is wrong. */
    error?: string;
    busy?: boolean;
}

/**
 * **Groups the secret in fours.** A base32 secret is 32 characters, and it exists to be
 * typed by hand when the scan fails: ungrouped, it is a typo factory, and the person
 * blames the code they entered rather than the transcription. The V3 mockup renders
 * `JBSW Y3DP EHPK 3PXP`, and it is right.
 *
 * Applied to display only: the secret is never re-parsed from this string.
 */
const groupBy4 = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim();

/**
 * The two steps of pairing an authenticator app, taken from the V3 mockup's
 * `o-appairage` modal: scan, then enter the code it displays.
 *
 * **Shared by the two places that pair a factor, and that is the point.** The profile
 * offers it as a choice, the barrier imposes it as a condition of entry: the CONTENT is
 * the same gesture and must not drift into two versions, while the FRAME differs and
 * belongs to each caller. So this component renders no title, no dialog, and no
 * buttons: the profile wraps it in a `Modal` with its cross and its « Annuler », the
 * barrier renders it in the page with neither, because a barrier one can close is not
 * one.
 *
 * **The numbered steps are not decoration**: they say there are two gestures and which
 * is which. Somebody who scans and then closes the modal has done half of it and
 * registered nothing, which is the mistake the numbering prevents.
 */
const TotpPairing = ({ uri, secret, code, onCode, error, busy }: Props) => (
    <>
        <div className="totp-step">
            <span className="totp-step-num" aria-hidden="true">
                1
            </span>
            <div>
                <b className="totp-step-title">{c('Totp pairing').t`Scan this code with your authenticator app`}</b>
                <div className="totp-qr">
                    <QRCodeSVG value={uri} size={158} marginSize={2} />
                </div>
                <span className="totp-key">{groupBy4(secret)}</span>
                <p className="totp-secret">{c('Totp pairing').t`Cannot scan? Enter this key by hand in your app.`}</p>
            </div>
        </div>

        <div className="totp-step">
            <span className="totp-step-num" aria-hidden="true">
                2
            </span>
            <div>
                <b className="totp-step-title">{c('Totp pairing').t`Enter the code your app displays`}</b>
                <div className="mt-3">
                    <OtpInput
                        value={code}
                        onValue={onCode}
                        length={TOTP_CODE_LENGTH}
                        disabled={busy}
                        error={Boolean(error)}
                        ariaLabel={c('Mfa label').t`6-digit verification code`}
                    />
                </div>
                <p className="mfa-statut" role="status">
                    {error}
                </p>
            </div>
        </div>
    </>
);

export default TotpPairing;
